import { EnquiriesSortOption, FilterOptions } from "./enquiries";

export interface EvoFeedSubscriptions {
  new_autoconfirmed_bookings: boolean;
  new_same_day_autoconfirmed_bookings: boolean;
  new_enquiries: boolean;
  user_mentions: boolean;
  payment_disputes: boolean;
}

/**
 * Relative date windows a preset can store instead of fixed dates.
 *
 * Ported from legacy's booking rail, which stored the token and resolved it on
 * click rather than freezing a range (`collins-ui/src/js/controllers/bookings.js:724-760`).
 *
 * Deliberate divergence: legacy's `next_7_days` resolved to today..today+7,
 * eight days, while `next_3_days` resolved to today..today+2, three days. That
 * inconsistency is a legacy bug, not a feature. Here both are inclusive of
 * today and span exactly what they say: `next_3_days` = today..today+2,
 * `next_7_days` = today..today+6.
 */
export const RELATIVE_DATE_RANGES = [
  "today",
  "next_3_days",
  "next_7_days",
  "not_past"
] as const;

export type RelativeDateRange = (typeof RELATIVE_DATE_RANGES)[number];

export const RELATIVE_DATE_RANGE_LABELS: Record<RelativeDateRange, string> = {
  today: "Today",
  next_3_days: "Next 3 days",
  next_7_days: "Next 7 days",
  not_past: "Today onwards"
};

/**
 * Narrowing guard for values read back off the server. Preferences are
 * user data persisted as free-form JSON, so an unknown token must degrade to
 * "no relative range" rather than throw.
 */
export const isRelativeDateRange = (
  value: unknown
): value is RelativeDateRange =>
  typeof value === "string" &&
  (RELATIVE_DATE_RANGES as readonly string[]).includes(value);

export interface ResolvedDateRange {
  date_from?: string;
  date_to?: string;
}

const toLocalIsoDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/**
 * Adds whole days in local time. Constructing through the local Date parts
 * (rather than adding milliseconds) keeps the result on the intended calendar
 * day across DST boundaries.
 */
const addLocalDays = (date: Date, days: number): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);

/**
 * Resolves a relative window to concrete `YYYY-MM-DD` bounds.
 *
 * Resolution is client-side and in the operator's local timezone, at the
 * moment the preset is applied — the server's timezone handling for these
 * bounds is not dependable.
 *
 * @param now injected clock; defaults to the real one. Tests must pass it.
 */
export const resolveRelativeDateRange = (
  range: RelativeDateRange,
  now: Date = new Date()
): ResolvedDateRange => {
  const today = toLocalIsoDate(now);

  switch (range) {
    case "today":
      return { date_from: today, date_to: today };
    case "next_3_days":
      return {
        date_from: today,
        date_to: toLocalIsoDate(addLocalDays(now, 2))
      };
    case "next_7_days":
      return {
        date_from: today,
        date_to: toLocalIsoDate(addLocalDays(now, 6))
      };
    case "not_past":
      // Open ended: everything from today forward.
      return { date_from: today, date_to: undefined };
  }
};

/**
 * Returns the relative window a fixed date range currently corresponds to, or
 * undefined when it matches none. Used to offer "keep this relative to today"
 * at save time.
 */
export const matchRelativeDateRange = (
  filters: Pick<FilterOptions, "date_from" | "date_to">,
  now: Date = new Date()
): RelativeDateRange | undefined =>
  RELATIVE_DATE_RANGES.find((range) => {
    const resolved = resolveRelativeDateRange(range, now);
    return (
      resolved.date_from === filters.date_from &&
      resolved.date_to === (filters.date_to ? filters.date_to : undefined)
    );
  });

export interface FilterPreset {
  id: string;
  name: string;
  filters: FilterOptions;
  sort?: EnquiriesSortOption;
  query?: string;
  /** Pinned presets are surfaced ahead of unpinned ones. */
  pinned?: boolean;
  /** Explicit display position. Lower sorts first; undefined sorts last. */
  order?: number;
  /**
   * When set, `filters.date_from` / `filters.date_to` are recomputed from this
   * token each time the preset is applied, so "This week" does not rot into
   * one fixed week. Absent means the stored dates are used verbatim, which is
   * how every preset saved before this field existed keeps behaving.
   */
  relative_date_range?: RelativeDateRange;
}

export interface UserPreferences {
  locale?: string;
  language?: string;
  evo_feed_subscriptions: EvoFeedSubscriptions;
  enquiry_filter_presets?: FilterPreset[];
  /**
   * Enquiry sidebar notification filter values the user has pinned.
   * Server-persisted per user so pins follow the operator between devices.
   */
  enquiry_pinned_notifications?: string[];
}

export type EvoFeedSubscriptionKey = keyof EvoFeedSubscriptions;

export const DEFAULT_USER_PREFERENCES: UserPreferences = {
  locale: "en-GB",
  language: "en-GB",
  evo_feed_subscriptions: {
    new_autoconfirmed_bookings: false,
    new_same_day_autoconfirmed_bookings: false,
    new_enquiries: false,
    user_mentions: false,
    payment_disputes: true
  },
  enquiry_filter_presets: [],
  enquiry_pinned_notifications: []
};
